import React, { Dispatch, ReactNode } from "react";

import usePublisher, { Recording } from "hooks/vonage/usePublisher";
import useSession from "hooks/vonage/useSession";

import {
  LiveParticipantType,
  LiveRoomAccessDetails,
} from "store/meetings/types";
import { SingleVoting } from "store/voting/types";

import { SidebarContent } from "../VideoProvider";
import useScreenShareListener from "hooks/vonage/useScreenShareListener";

export const VonageContext = React.createContext<{
  connected: boolean;
  session: (OT.Session & { streams?: OT.Stream[] }) | null;
  publisher: OT.Publisher | null;
  connect: (accessDetails: LiveRoomAccessDetails) => void;
  toggleAudio: () => void;
  toggleVideo: () => void;
  audioMuted: boolean;
  videoMuted: boolean;
  disconnect: () => void;
  isConnecting: boolean;
  subscribers: OT.Subscriber[];
  subscribersCount: number;
  enableScreenShare: () => void;
  recording: Recording;
  setRecording: Dispatch<React.SetStateAction<Recording>>;
  isCurrUserScreenShare: boolean;
  screenShareEnabled: boolean;
  agendaOpened: boolean;
  sidebarContent: SidebarContent;
  setSidebarContent: (content: SidebarContent) => void;
  setAgendaOpened: (opened: boolean) => void;
  agendaSmall: boolean;
  setAgendaSmall: (small: boolean) => void;
  liveVoting: SingleVoting | null;
  setLiveVoting: (voting: SingleVoting | null) => void;
  remoteScreenShareEnabled: boolean;
  liveParticipants: LiveParticipantType[];
  initPublisher: () => void;
}>(null!);

type VideoProviderProps = {
  children: ReactNode;
};

const VonageProvider = ({ children }: VideoProviderProps) => {
  const {
    connected,
    session,
    connect,
    disconnect,
    isConnecting,
    subscribers,
    subscribersCount,
    sidebarContent,
    setSidebarContent,
    agendaOpened,
    setAgendaOpened,
    agendaSmall,
    setAgendaSmall,
    liveVoting,
    setLiveVoting,
    remoteScreenShareEnabled,
    liveParticipants,
  } = useSession();
  const {
    publisher,
    toggleAudio,
    toggleVideo,
    audioMuted,
    videoMuted,
    enableScreenShare,
    screenShareEnabled,
    isCurrUserScreenShare,
    initPublisher,
    setShareScreen,
    recording,
    setRecording,
  } = usePublisher(session);

  useScreenShareListener(session, setShareScreen);

  return (
    <VonageContext.Provider
      value={{
        connected,
        subscribersCount,
        subscribers,
        isConnecting,
        disconnect,
        session,
        publisher,
        connect,
        toggleAudio,
        toggleVideo,
        audioMuted,
        videoMuted,
        enableScreenShare,
        isCurrUserScreenShare,
        screenShareEnabled,
        recording,
        setRecording,
        sidebarContent,
        setSidebarContent,
        agendaOpened,
        setAgendaOpened,
        agendaSmall,
        setAgendaSmall,
        liveVoting,
        setLiveVoting,
        remoteScreenShareEnabled,
        liveParticipants,
        initPublisher,
      }}
    >
      {children}
    </VonageContext.Provider>
  );
};

export default VonageProvider;
